import { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../constants';

export interface Model {
  id: number;
  name: string;
  display_name: string;
  context_window: number;
  input_cost_per_million_tokens: number | null;
  output_cost_per_million_tokens: number | null;
  description: string;
  provider: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useModels = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const url = new URL('/api/models', API_ENDPOINTS.BASE_URL);
        const response = await fetch(url.toString(), {
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch models');
        }
        
        const data: Model[] = await response.json();
        setModels(data);
      } catch (err) {
        console.error('Error fetching models:', err instanceof Error ? err.message : 'Unknown error');
        setError(err instanceof Error ? err.message : 'Failed to fetch models');
        // Fallback to hardcoded models if API fails
        setModels([
          {
            id: 1,
            name: 'gpt-4',
            display_name: 'GPT-4',
            context_window: 8192,
            input_cost_per_million_tokens: 30.00,
            output_cost_per_million_tokens: 60.00,
            description: '8,192 tokens • $0.03/1K tokens',
            provider: 'OpenAI',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 2,
            name: 'gpt-3.5-turbo',
            display_name: 'GPT-3.5 Turbo',
            context_window: 4096,
            input_cost_per_million_tokens: 1.50,
            output_cost_per_million_tokens: 2.00,
            description: '4,096 tokens • $0.0015/1K tokens',
            provider: 'OpenAI',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 3,
            name: 'claude-3-opus',
            display_name: 'Claude 3 Opus',
            context_window: 200000,
            input_cost_per_million_tokens: 15.00,
            output_cost_per_million_tokens: 75.00,
            description: '200K tokens • $15/1M tokens',
            provider: 'Anthropic',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 4,
            name: 'claude-3-sonnet',
            display_name: 'Claude 3 Sonnet',
            context_window: 200000,
            input_cost_per_million_tokens: 3.00,
            output_cost_per_million_tokens: 15.00,
            description: '200K tokens • $3/1M tokens',
            provider: 'Anthropic',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 5,
            name: 'claude-3-haiku',
            display_name: 'Claude 3 Haiku',
            context_window: 200000,
            input_cost_per_million_tokens: 0.25,
            output_cost_per_million_tokens: 1.25,
            description: '200K tokens • $0.25/1M tokens',
            provider: 'Anthropic',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 6,
            name: 'gemini-pro',
            display_name: 'Gemini Pro',
            context_window: 32000,
            input_cost_per_million_tokens: 0.00,
            output_cost_per_million_tokens: 0.00,
            description: '32K tokens • Free tier available',
            provider: 'Google',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 7,
            name: 'llama-2-70b',
            display_name: 'Llama 2 70B',
            context_window: 4096,
            input_cost_per_million_tokens: 0.00,
            output_cost_per_million_tokens: 0.00,
            description: '4,096 tokens • Open source',
            provider: 'Meta',
            is_active: true,
            created_at: '',
            updated_at: ''
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  return { models, loading, error };
};